import { FeatureFlagKeys, useFeatureFlag } from '@modules/FeatureFlagModule';
import useInboxState from '@modules/InboxModule/hooks/useInboxState';
import useVoiceTask from '@modules/TasksModule/hooks/useVoiceTask';
import { ITask } from '@modules/TasksModule/types';
import { useCallback } from 'react';
import noop from '../../../../../lib/utils/noop';

const TRANSFER_RINGING_THRESHOLD_SECONDS = 6;

export function useTaskRowVoiceProps(
  task: ITask,
  active: boolean,
  onClick: () => void = noop,
) {
  const { showClassificationModalForTaskId } = useInboxState();
  const {
    isHeld,
    isOffered,
    isMissed,
    isWrapUp,
    isInternal,
    isInbound,
    isConnecting,
    displayName,
    isAccepted,
    isAnswering,
    isIncomingTransfer,
    transferStatus,
    isSelfTransfer,
    escalationSummary,
  } = useVoiceTask(task);

  // Gated here rather than in the card so every surface built on this hook (inbox card,
  // PiP) inherits the same rule, and so the card stays presentational. (VAH-1354)
  const isEscalationSummaryEnabled = useFeatureFlag(
    FeatureFlagKeys.featureVaEscalationSummary,
  );

  const showClassificationModal = showClassificationModalForTaskId === task.id;
  const headLineClassName = isMissed ? 'c-red-500' : '';

  const handleClick = useCallback(
    (event: React.SyntheticEvent) => {
      event.stopPropagation();
      onClick();
    },
    [onClick],
  );

  const showInternalTag = isInternal && isInbound;

  const shouldShowTenantTag =
    !!task.tenantId && (task.attributes.shouldClassify || !showInternalTag);

  const shouldShowQueueTag = !!task.queueName && !isAccepted && !isWrapUp;

  const isTransferring = !!transferStatus?.inProgress;
  const secondsSinceTransferStarted =
    transferStatus?.initiatedAt instanceof Date
      ? (Date.now() - transferStatus.initiatedAt.getTime()) / 1000
      : 0;

  const transferText = `Transfer in progress...${
    secondsSinceTransferStarted >= TRANSFER_RINGING_THRESHOLD_SECONDS
      ? ' Ringing'
      : ''
  }`;

  const campaignName = task.attributes.campaign?.name;
  const workflowName = task.attributes.workflow?.name;

  return {
    task,
    active,
    displayName,
    headLineClassName,
    showClassificationModal,
    showInternalTag,
    shouldShowTenantTag,
    shouldShowQueueTag,
    isMissed,
    isHeld,
    isWrapUp,
    isAccepted,
    isInbound,
    isAnswering,
    isConnecting,
    isOffered,
    isIncomingTransfer,
    isTransferring,
    transferText,
    campaignName,
    workflowName,
    isInternal,
    isSelfTransfer,
    escalationSummary: isEscalationSummaryEnabled
      ? escalationSummary
      : undefined,
    handleClick,
    time: task.startedAt,
  };
}
